import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@intelliauth/ui'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'

export const REGIONS = [
  { value: 'local', label: 'Local' },
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU West (Ireland)' },
  { value: 'eu-central-1', label: 'EU Central (Frankfurt)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
]

type Plan = {
  id: string
  name: string
  tier?: string
  billing_period: 'free' | 'monthly' | 'annual'
}

export default function CreateTenantDialog({
  open,
  orgId,
  onOpenChange,
  onSuccess,
}: {
  open: boolean
  orgId: string
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [region, setRegion] = useState('local')
  const [planId, setPlanId] = useState<string>('')
  const [adminEmail, setAdminEmail] = useState('')
  const [slugTouched, setSlugTouched] = useState(false)
  const [creating, setCreating] = useState(false)
  const [plans, setPlans] = useState<Plan[]>([])
  const [plansLoading, setPlansLoading] = useState(false)

  // Live plans — fetched on dialog open. Cached in-process for the
  // dialog's lifetime; closes + reopens re-fetch (cheap, 60s server cache).
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPlansLoading(true)
    cpClient()
      .get('/api/v1/plans', {})
      .then((res) => {
        if (cancelled) return
        // Envelope: { data: Plan[], meta: {...} } — cpClient.get() returns
        // the full envelope. data is the Plan array directly (not nested).
        const list = ((res as { data?: Plan[] })?.data ?? []) as Plan[]
        const publicPlans = list.filter((p) => (p as Plan & { is_public?: boolean }).is_public !== false)
        setPlans(publicPlans)
        if (publicPlans.length > 0) {
          setPlanId((prev) => prev || publicPlans[0].id)
        }
      })
      .catch(() => {
        // Soft-fail: leave plans empty, backend will use org default.
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    if (open) {
      setName('')
      setSlug('')
      setRegion('local')
      setPlanId('')
      setAdminEmail('')
      setSlugTouched(false)
    }
  }, [open])

  useEffect(() => {
    if (!slugTouched) {
      setSlug(
        name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 63),
      )
    }
  }, [name, slugTouched])

  const isValidSlug = /^[a-z][a-z0-9-]{0,61}[a-z0-9]$/.test(slug) || slug.length === 0
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim() || !region || !adminEmail.trim()) return
    setCreating(true)
    try {
      // admin_emails is a comma-separated list in the form; backend
      // accepts an array with at least one entry. Whitespace + duplicates
      // are tolerated — the server canonicalises via SanitizeAdminEmails.
      const adminEmails = adminEmail
        .split(/[,\n]/)
        .map(s => s.trim())
        .filter(s => s.length > 0)
      await cpClient().post('/api/v1/organizations/{id}/tenants', {
        path: { id: orgId },
        body: {
          name: name.trim(),
          slug: slug.trim(),
          region,
          plan_id: planId || undefined,
          admin_emails: adminEmails,
        },
      })
      toast.success(`Tenant "${name}" created — provisioning started`)
      onSuccess()
    } catch (err) {
      const msg =
        err instanceof CpV2ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create tenant'
      toast.error(msg)
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create a tenant</DialogTitle>
          <DialogDescription>
            Each tenant is an isolated authentication environment with its own users, sessions, and
            configuration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="tenant-name"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Name
            </label>
            <Input
              id="tenant-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Production"
              maxLength={100}
            />
          </div>

          <div>
            <label
              htmlFor="tenant-slug"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Slug
            </label>
            <Input
              id="tenant-slug"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value.toLowerCase())
                setSlugTouched(true)
              }}
              placeholder="production"
              maxLength={63}
              aria-invalid={!isValidSlug}
            />
            <p className="mt-1 text-xs text-muted-foreground/70">
              Lowercase letters, numbers, and hyphens. Used in tenant URLs.
            </p>
          </div>

          <div>
            <label
              htmlFor="tenant-region"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Region
            </label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger id="tenant-region">
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {REGIONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label
              htmlFor="tenant-plan"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Plan
            </label>
            <Select value={planId} onValueChange={setPlanId} disabled={plansLoading || plans.length === 0}>
              <SelectTrigger id="tenant-plan">
                <SelectValue placeholder={plansLoading ? 'Loading plans…' : 'Select a plan'} />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.billing_period === 'free' ? ' · Free' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground/70">
              Defaults to your org's plan if unselected.
            </p>
          </div>

          <div>
            <label
              htmlFor="tenant-admin-email"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              Admin email
            </label>
            <Input
              id="tenant-admin-email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="admin@example.com"
              aria-invalid={!isValidEmail && adminEmail.length > 0}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              An activation email will be sent to this address. They'll set their own password — you
              won't see it.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreate}
            disabled={!name.trim() || !isValidSlug || !region || !isValidEmail}
            loading={creating}
            leftIcon={Plus}
          >
            Create tenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
